import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/store/Store";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  AlertColor,
} from "@mui/material";
import MWExceptionList from "../../../../component/MWExceptionList";
import LoopIcon from "@mui/icons-material/Loop";
import {
  GetPatientNoteListBody,
  NoteList,
} from "../../../../redux/Notes/PatientNotesList/Model";
import dayjs from "dayjs";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import DeletePatientNote from "../DeletePatientNote";
import { getAdminNoteListByPatient } from "../../../../redux/Notes/PatientNotesList/API";
import { clearNoteAPIRes } from "../../../../redux/Notes/updateNoteStatus/API";
import MWSnackbar from "../../../../component/MWSnackbar";
import { clearAddNoteAPIRes } from "../../../../redux/Notes/addNotes/API";
type Props = {
  activeNoteLoader: boolean | any;
};
export default function ActivePatientNoteList({ activeNoteLoader }: Props) {
  const dispatch = useDispatch();
  const { profileId } = useParams() as {
    profileId: string;
  };
  const [updateNoteStatusDialog, setUpdateNoteStatusDialog] = useState(false);
  const updateNoteStatusEditDialogUpdateState = (passedVal: boolean) => {
    setUpdateNoteStatusDialog(passedVal);
  };

  // Patient Notes List Api Value from store
  const patientNoteListResVal = useSelector(
    (state: AppState) => state.patientNoteListRes
  );

  const [noteList, setNoteList] = useState([] as any[]);
  const [selectedItem, setSelectedItem] = useState("");
  useEffect(() => {
    activeNoteLoader(patientNoteListResVal?.loading);
    if (patientNoteListResVal?.getPatientNoteList?.noteList !== undefined) {
      if (patientNoteListResVal?.getPatientNoteList?.noteList.length > 0) {
        let noteItem = patientNoteListResVal?.getPatientNoteList?.noteList.map(
          (item: NoteList) => ({
            id: item.id,
            note:
              item?.note !== undefined
                ? item?.note !== null
                  ? item?.note !== ""
                    ? item.note
                    : ""
                  : ""
                : "",
            createdDate:
              item?.createdDate !== undefined
                ? item?.createdDate !== null
                  ? dayjs(item.createdDate).format("ddd, DD/MM/YYYY")
                  : ""
                : "",
            createdBy:
              item?.createdBy?.user?.first_name !== undefined &&
              item?.createdBy?.user?.last_name !== undefined
                ? item?.createdBy?.user?.first_name +
                  " " +
                  item?.createdBy?.user?.last_name
                : "",
            modifiedDate:
              item?.modifiedDate !== undefined
                ? item?.modifiedDate !== null
                  ? dayjs(item.modifiedDate).format("ddd, DD/MM/YYYY")
                  : ""
                : "",
            modifiedBy:
              item?.modifiedBy?.user?.first_name !== undefined &&
              item?.modifiedBy?.user?.last_name !== undefined
                ? item?.modifiedBy?.user?.first_name +
                  " " +
                  item?.modifiedBy?.user?.last_name
                : "",
          })
        );
        setNoteList(noteItem);
      } else setNoteList([] as any[]);
    } else setNoteList([] as any[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientNoteListResVal]);
  const columns: GridColDef[] = [
    { field: "note", headerName: "Note", flex: 1 },
    { field: "createdDate", headerName: "Created Date", minWidth: 140 },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
    },
    { field: "modifiedBy", headerName: "Modified By", flex: 1 },
    { field: "modifiedDate", headerName: "Modified Date", minWidth: 140 },
    {
      field: "id",
      headerName: "Modify Status",
      minWidth: 35,
      renderCell: (params: GridCellParams) => (
        <Button
          startIcon={<LoopIcon />}
          variant="outlined"
          onClick={() => {
            toggleNoteStatus(params.value);
          }}
          sx={{ textTransform: "none" }}
          disabled={updateNoteStatusDialog}
        >
          Edit
        </Button>
      ),
    },
  ];
  // Notes Status change API
  function toggleNoteStatus(item: number) {
    setUpdateNoteStatusDialog(true);
    setSelectedItem(String(item));
  }
  const [addUpdateNoteStatusSuccessMsg, setAddUpdateNoteStatusSuccessMsg] =
    useState("");
  const [addNoteErrMsgType, setUpdateNoteStatusErrMsgType] =
    useState<AlertColor>("success");
  const addNotesVal = useSelector((state: AppState) => state.addNoteRes);
  useEffect(() => {
    if (addNotesVal?.successMsg !== "") {
      setAddUpdateNoteStatusSuccessMsg(addNotesVal?.successMsg);
      setUpdateNoteStatusErrMsgType("success");
      dispatch(
        getAdminNoteListByPatient({
          patientId: profileId,
          isActive: true,
        } as GetPatientNoteListBody)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNotesVal]);

  // Update Notes status Api Value from store
  const updateNoteResVal = useSelector(
    (state: AppState) => state.updateNoteRes
  );

  useEffect(() => {
    if (updateNoteResVal?.successMsg !== "") {
      setAddUpdateNoteStatusSuccessMsg(updateNoteResVal?.successMsg);
      setUpdateNoteStatusErrMsgType("success");
      dispatch(
        getAdminNoteListByPatient({
          patientId: profileId,
          isActive: true,
        } as GetPatientNoteListBody)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteResVal]);

  const closeModifyNoteStatusAlert = (passedVal: boolean) => {
    if (passedVal) {
      dispatch(clearNoteAPIRes());
      dispatch(clearAddNoteAPIRes());
      setAddUpdateNoteStatusSuccessMsg("");
    }
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title={
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>Note List</Grid>
            </Grid>
          }
        />
        <CardContent>
          {noteList.length > 0 ? (
            <DataGrid
              rows={noteList}
              columns={columns}
              loading={patientNoteListResVal?.loading}
              pageSize={10}
              hideFooter
              hideFooterPagination
              disableSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
              autoHeight
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
            />
          ) : (
            <MWExceptionList />
          )}
        </CardContent>
      </Card>
      {updateNoteStatusDialog ? (
        <DeletePatientNote
          status={false}
          selectedItem={selectedItem}
          updateNoteStatusDialog={updateNoteStatusDialog}
          updateNoteStatusEditDialogUpdateState={
            updateNoteStatusEditDialogUpdateState
          }
        />
      ) : null}
      <MWSnackbar
        msg={addUpdateNoteStatusSuccessMsg}
        type={addNoteErrMsgType}
        alertClose={closeModifyNoteStatusAlert}
      />
    </Box>
  );
}
